import { type JanusCtx, type Row, ec, ecId, ecLean, handBack, insertRoll, itemField, record, requiredInt, upper, definitionNotHeld, refuse } from '../context.js';
import { abilityEntryId, rawAbilityChargeEntry } from './charge-entry.js';
import { manaSpendPreDraw, fireManaSpend } from '../mana-spend-trigger.js';
import { billChargedCastSource } from '../charged-cast.js';
import { HOLDING_MASTERY, MASTERY_POOL, masteryMarkerPreDraw, touchMasteryMarker } from './mastery-marker.js';
import { encounterInCombat, ETHER_MECHANIC_ID, PSI_MECHANIC_ID } from '../../economy/out-of-combat.js';
import { phpJsonInt } from '../../compile/php-json-values.js';
import { distinctUuids } from '../../reactions/trigger-dispatch.js';
import { ORB_SLOTS_ID, ORB_QUEUE_MAX_SLOTS } from '../../charges/charge-adjust.js';
import { statusItemId } from '../../statuses/expiry.js';
import { layerListEntryUuid } from '../statuses/status-trigger-gate.js';
import type { JanusInput } from '../janus.js';
import { consumeNestedUnitPerUse } from '../consume-nested-unit.js';
import { chargeReductionDelta } from '../../resources/reduction-guard.js';
import { logPreventedReduction, resourceReductionReaders, setMana } from '../resource-reduction.js';

/** Before any effect dice, preview a negative change_caster_mana (its actual debit is checked after effects). */
export function abilityManaChangePreDraw(ctx: JanusCtx, ability: Row, caster: Row, variable: unknown): void {
  if (!ctx.read.has(ability, 'change_caster_mana')) return;
  if (manaChangeDelta(ctx, ability, variable) < 0) manaSpendPreDraw(ctx, caster, baseSpellLevel(ctx, ability));
}
function baseSpellLevel(ctx: JanusCtx, ability: Row): number | null {
  return itemField(ability, 'item_type') === 'spell' ? Number(ctx.read.value(ability, 'spell_level') ?? 0) : null;
}
function manaChangeDelta(ctx: JanusCtx, ability: Row, variable: unknown): number {
  let raw = String(ctx.read.value(ability, 'change_caster_mana') ?? '');
  if (variable != null && String(variable).trim() !== '' && !Number.isNaN(Number(variable)))
    raw = raw.split('[x]').join(String(Math.trunc(Number(variable))));
  if (!/^\s*[+-]?\d+\s*$/.test(raw)) handBack('janus_post_roll_change_ability_resources:mana-formula');
  return parseInt(raw, 10);
}

/** adjust_charges.php: the first matching caster ability entry receives a clamped delta. */
export function adjustCharges(ctx: JanusCtx, caster: Row, itemId: string, delta: number, logMessage: boolean): number | null {
  if (!Number.isSafeInteger(delta)) handBack('adjust_charges:invalid-delta');
  const id = upper(itemId);
  // adjust_charges.php ~L146: every Mastery change stamps / refreshes the Holding Mastery marker (ft_touch_mastery_marker — a status row
  // the upkeep reads). Ported 2026-10-09 (mastery-marker.ts); a caller whose commit cannot persist it keeps the named hand-back.
  const { entry, current, max: rawMax } = rawAbilityChargeEntry(ctx, caster, id);
  if (!entry) return null;
  if (delta === 0) return current;
  if (delta < 0 && chargeReductionDelta(caster, delta, resourceReductionReaders(ctx)) === 0) {
    logPreventedReduction(ctx, caster, 'charges');
    return current;
  }
  let max = id === ORB_SLOTS_ID ? ORB_QUEUE_MAX_SLOTS : Math.max(1, rawMax);
  if (delta < 0 && current > max && projectilePool(ctx, caster, id, current)) max = current;
  const next = Math.max(0, Math.min(current + delta, max));
  if (next < current && chargeReductionDelta(caster, next - current, resourceReductionReaders(ctx)) === 0) {
    logPreventedReduction(ctx, caster, 'charges');
    return current;
  }
  if (id === MASTERY_POOL) masteryMarkerPreDraw(ctx, caster);
  (entry.charges = record(entry.charges) ?? {}).current = next;
  // adjust_charges.php:79 picks an INVENTORY holder first (an item-row caster's own pool), then the encounter creature.
  if (record(caster.inventory)?.inventory_id != null) handBack('adjust_charges:inventory-holder');
  const holder = ecId(caster);
  if (!holder) handBack('adjust_charges:holder-not-held');
  const prior = ctx.chargesChanged.find(change => change.holder === holder && change.item_id === id);
  ctx.chargesChanged.push({ holder, item_id: id, before: prior?.before ?? current, charges: next });
  // gated on the REQUESTED delta (a grant into a full pool still counts as the pool moving), after the write, before the log row
  if (id === MASTERY_POOL && delta !== 0) touchMasteryMarker(ctx, caster);
  if (logMessage) insertRoll(ctx, { roll_type_1: 'adjust_charges', roll_item_id: id, by: caster, roll_result: delta, mod: delta });
  return next;
}

// AbilityType::getAll() — the buckets consume_ability_charges() scans (no innate_spell_per_long_rest, unlike adjust_charges).
const CONSUME_BUCKETS = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'lair_ability', 'trap_ability',
  'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta', 'sanguine', 'ability_choice'] as const;
/** PHP 8 is_numeric() on a string: optional surrounding whitespace, a sign, a decimal or exponent form — never hex / binary / INF. */
const PHP_NUMERIC = /^\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?\s*$/;

/** ft_projectile_pool(): follow the free ability's actual granting status, never infer it from the count. */
function projectilePool(ctx: JanusCtx, caster: Row, id: string, remaining: number): boolean {
  if (remaining < 0 || remaining > 1000) return false;
  for (const value of ctx.read.entries(caster, 'free_ability')) {
    const entry = record(value);
    if (abilityEntryId(value) !== id || !entry) continue;
    for (const status of ctx.read.statuses(caster)) {
      if (!entry.source || upper(status.status_id) !== upper(entry.source)) continue;
      const embedded = record(status.status_arr);
      const parent = embedded && (record(embedded.item_layer_types) || record(embedded.computed))
        ? embedded : ctx.read.definition(statusItemId(status));
      if (!parent) definitionNotHeld('adjust_charges:definition-not-held', statusItemId(status));
      if (ctx.read.has(parent, 'spell_upcast_additional_projectiles_created')
          && distinctUuids(ctx.read.entries(parent, 'free_ability')).some(child => upper(child) === id)) return true;
    }
  }
  return false;
}

function layerEntryUuid(value: unknown): string {
  return layerListEntryUuid(value);
}

/** Shared charge-spend waiver, including upkeep starvation at an empty pool. */
export function chargeSpendWaivedPools(ctx: { read: Pick<JanusCtx['read'], 'definition' | 'entries'> }, statuses: readonly Row[]): Set<string> {
  return new Set(statuses.flatMap(status => {
    const def = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
    if (!def) handBack('consume_ability_charges:waiver-status-not-held');
    return ctx.read.entries(def, 'ability_charges_not_required').map(layerEntryUuid);
  }));
}

/** ability_charge_rule_layers_first_failure.php:consume_ability_charges — the LINKED pool spend (a menta's Psi, a Force Choke's pool).
 *  `apply` false = the pre-draw proof (preflightPostRollChargeResources): every lookup and named hand-back, nothing written. */
function consumeAbilityCharges(ctx: JanusCtx, ability: Row, caster: Row, useAbilityVariable1: unknown, apply = true): Record<string, number> {
  const debits: Record<string, number> = {};
  if (!ctx.read.has(ability, 'consumes_ability_charges')) return debits;
  // janus_item_layers_for_ability_charge_rules(): an attack resolution nests the ability under the weapon → PHP.
  if (ctx.read.has(ability, 'attack_ability_arr')) handBack('consume_ability_charges:attack-ability-arr');
  if (record(caster.inventory)?.inventory_id != null) handBack('adjust_charges:inventory-holder');
  // ft_ability_charge_spend_waived_by_status(): the caster as loaded, before this use's status writes.
  const waivers = chargeSpendWaivedPools(ctx, ctx.read.statuses(caster));
  for (const wantedRaw of ctx.read.entries(ability, 'consumes_ability_charges')) {
    const wanted = layerEntryUuid(wantedRaw);
    if (!wanted) handBack('consume_ability_charges:entry-shape');
    if (waivers.has(wanted)) continue;
    // Ether and Psi spend only in combat or when the encounter's combat state is unknown.
    if ((wanted === PSI_MECHANIC_ID || wanted === ETHER_MECHANIC_ID) && encounterInCombat(ctx.encounter) === false) continue;
    for (const bucket of CONSUME_BUCKETS) {
      for (const value of ctx.read.entries(caster, bucket)) {
        if (abilityEntryId(value) !== wanted) continue;
        // ft_resolve_ability_charge_qty(): [x] → a numeric use_ability_variable_1, else the item's menta_level / spell_level, else 1;
        // [max] / [any] → the pool's ceiling / current count (ability_entry_with_current_charges re-reads the persisted row).
        const raw = ctx.read.value(ability, 'consumes_charges_qty');
        const token = raw === true ? '[x]' : String(raw ?? '');
        let qty: number;
        if (token === '[x]') {
          const v1 = useAbilityVariable1;
          // PHP is_numeric() (no hex / binary / 'Infinity' — Codex review 2026-10-09: Number('0x10') is 16, is_numeric('0x10') is false)
          if (typeof v1 === 'number' ? Number.isFinite(v1) : typeof v1 === 'string' && PHP_NUMERIC.test(v1)) qty = Math.trunc(Number(v1));
          else {
            const level = Math.trunc(Number(ctx.read.value(ability, 'menta_level') ?? ctx.read.value(ability, 'spell_level') ?? 0)) || 0;
            qty = level > 0 ? level : 1;
          }
        } else if (token === '[max]') {
          // ability_entry_max_charges() of THIS iterated entry (a pool held in two buckets can carry two ceilings — Codex review 2026-09-29)
          const row = record(value);
          qty = requiredInt(record(row?.charges)?.max ?? row?.max_ability_charges, 'consume_ability_charges:max-not-held');
        } else if (token === '[any]') {
          qty = rawAbilityChargeEntry(ctx, caster, wanted).current; // ability_entry_with_current_charges(): the persisted / live count
        } else qty = phpJsonInt(raw);
        if (qty! < 1 && token !== '[any]' && token !== '[max]') qty = 1;
        // adjustCharges' own delta check, asked here so the pre-draw pass refuses what the spend would (Codex review 2026-10-09)
        if (!Number.isSafeInteger(qty!)) handBack('adjust_charges:invalid-delta');
        if (!apply) {
          chargeSpendPreDraw(ctx, caster, wanted, -qty!);
          debits[wanted] = (debits[wanted] ?? 0) + qty!;
          continue;
        }
        const before = rawAbilityChargeEntry(ctx, caster, wanted).current;
        const after = adjustCharges(ctx, caster, wanted, -qty!, true);
        if (after != null && after < before) debits[wanted] = (debits[wanted] ?? 0) + before - after;
      }
    }
  }
  return debits;
}

/** Optional triggers with an exhausted linked pool do not offer or block the inciting action.
 * Use settlement's quantities and waivers, and the live charge reader (including earlier frame spends). */
export function triggerLinkedChargesAvailable(ctx: JanusCtx, ability: Row, caster: Row): boolean {
  for (const entry of ctx.read.entries(ability, 'consumes_ability_charges'))
    rawAbilityChargeEntry(ctx, caster, layerEntryUuid(entry));
  return Object.entries(consumeAbilityCharges(ctx, ability, caster, null, false)).every(([id, qty]) =>
    chargeReductionDelta(caster, -qty, resourceReductionReaders(ctx)) === 0
      || rawAbilityChargeEntry(ctx, caster, id).current >= qty);
}

/** creature_ability_charge_pool_spends_on_this_use(): an instant volley's CHILD never spends its own pool (checked first), nor does a
 *  status-only Last Stand ward (ctx.statusChargeFrame — the status is the charge); a trigger frame always spends (janus_trigger() already
 *  gated the charges); else in combat, or a pool with no turn-recharge window. */
function ownPoolSpends(ctx: JanusCtx, ability: Row, caster: Row, id: string): boolean {
  const spends = ctx.read.has(ability, 'ability_charges') && ctx.instantChild !== id && ctx.statusChargeFrame !== id
    && (!!ctx.trigger || ctx.read.inCombat(ctx.encounter, caster)
      || !(ctx.read.has(ability, 'recharge_range_begin') && ctx.read.has(ability, 'recharge_range_end')));
  return spends && !(id === ETHER_MECHANIC_ID && encounterInCombat(ctx.encounter) === false);
}

/** What adjustCharges() would meet for one pool, without writing: the entry and its counts held, the holder a creature, and (Mastery)
 *  the marker touch persistable. Every refusal is the spend's own named hand-back. */
function chargeSpendPreDraw(ctx: JanusCtx, caster: Row, id: string, delta = -1): void {
  if (delta < 0 && chargeReductionDelta(caster, delta, resourceReductionReaders(ctx)) === 0) return;
  if (id === MASTERY_POOL) masteryMarkerPreDraw(ctx, caster);
  const { current, max } = rawAbilityChargeEntry(ctx, caster, id);
  if (delta < 0 && current > (id === ORB_SLOTS_ID ? ORB_QUEUE_MAX_SLOTS : Math.max(1, max))) projectilePool(ctx, caster, id, current);
  if (record(caster.inventory)?.inventory_id != null) handBack('adjust_charges:inventory-holder');
  if (!ecId(caster)) handBack('adjust_charges:holder-not-held');
}

/** Prove own and linked charge spends before dice, using settlement's same loaded-caster waivers and held counts. */
export function preflightPostRollChargeResources(ctx: JanusCtx, ability: Row, caster: Row, useAbilityVariable1: unknown = null): void {
  const id = upper(itemField(ability, 'item_id'));
  if (ownPoolSpends(ctx, ability, caster, id)) chargeSpendPreDraw(ctx, caster, id);
  consumeAbilityCharges(ctx, ability, caster, useAbilityVariable1, false);
}

/** janus_post_roll_change_ability_resources.php: ability_charges and consumes_ability_charges. */
export function janusPostRollChangeAbilityResources(ctx: JanusCtx, ability: Row, caster: Row, useAbilityVariable1: unknown = null,
  attackHit?: boolean, input?: JanusInput): void {
  const id = upper(itemField(ability, 'item_id'));
  const debits: Record<string, number> = {};
  if (ownPoolSpends(ctx, ability, caster, id)) {
    const before = rawAbilityChargeEntry(ctx, caster, id).current;
    const after = adjustCharges(ctx, caster, id, -1, true);
    if (after != null && after < before) debits[id] = before - after;
  }
  for (const [poolId, qty] of Object.entries(consumeAbilityCharges(ctx, ability, caster, useAbilityVariable1)))
    debits[poolId] = (debits[poolId] ?? 0) + qty;
  // Record the exact spend for a later committed spell's Efficiency Core refund.
  if (itemField(ability, 'item_type') === 'sanguine'
      && (ctx.read.has(ability, 'counterspell_against_dis')
        || Object.keys(record(ability.item_layer_types) ?? {}).some(key => key.startsWith('next_spell_')))) {
    const carrier = ctx.statusesAdded.find(s => upper(s.status_item_id) === id && upper(s.target) === ecId(caster)
      && upper(s.caster) === ecId(caster) && s.status_connected_id === ctx.statusConnectedId);
    if (carrier && carrier.status_casting_targets_arr == null) carrier.status_casting_targets_arr = JSON.stringify({ t: '',
      sanguine_charge_receipt: { version: 1, item_id: id, connection_id: ctx.statusConnectedId, debits, refunded: false } });
  }
  // consume_inventory_charges() (janus_post_roll_change_ability_resources.php ~L95): a charged-item cast pays its source row's charges
  // (Wizard v9 WP5, charged-cast.ts). The timed FINISH bills in settleSpellResources instead (its source is the casting status's).
  if (input) consumeNestedUnitPerUse(ctx, input, ability, caster);
  billChargedCastSource(ctx, ability, caster, useAbilityVariable1);
  // change_caster_mana (janus_post_roll_change_ability_resources.php ~L93): + discern_variable_tags(value, caster, numeric variable_1),
  // capped at spirit. An attack's mana change requires its settled hit; linked charges above are spent even on a miss. It was not
  // modelled at all before (Codex review 2026-09-29: Restore Phase resolved without its +10 once the linked pool stopped handing back).
  if (ctx.read.has(ability, 'change_caster_mana')) {
    const type = String(itemField(ability, 'item_type') ?? '');
    const hitOnly = ctx.read.has(ability, 'consumes_mana_on_hit') || type === 'attack_ability' || type === 'opportunity_attack_ability';
    if (hitOnly && attackHit === undefined)
      handBack('janus_post_roll_change_ability_resources:mana-on-hit-gate');
    if (!hitOnly || attackHit) {
      const delta = manaChangeDelta(ctx, ability, useAbilityVariable1);
      const current = requiredInt(ecLean(caster, 'mana'), 'janus_post_roll_change_ability_resources:mana-not-held');
      const spirit = requiredInt(ctx.read.value(caster, 'spirit'), 'janus_post_roll_change_ability_resources:spirit-not-held');
      const after = setMana(ctx, caster, Math.min(current + delta, spirit));
      const spellLevel = baseSpellLevel(ctx, ability);
      if (delta < 0 && after < current) manaSpendPreDraw(ctx, caster, spellLevel);
      if (delta < 0) fireManaSpend(ctx, caster, current, after, spellLevel);
    }
  }
  if (ctx.read.has(ability, 'change_caster_luck')) {
    const type = String(itemField(ability, 'item_type') ?? '');
    const hitOnly = type === 'attack_ability' || type === 'opportunity_attack_ability';
    if (hitOnly && attackHit === undefined)
      handBack('janus_post_roll_change_ability_resources:attack-luck-hit-gate');
    if (!hitOnly || attackHit) {
      const delta = requiredInt(ctx.read.value(ability, 'change_caster_luck'), 'janus_post_roll_change_ability_resources:luck-delta');
      const current = requiredInt(ecLean(caster, 'luck'), 'janus_post_roll_change_ability_resources:luck-not-held');
      const fortune = requiredInt(ctx.read.value(caster, 'fortune'), 'janus_post_roll_change_ability_resources:fortune-not-held');
      ec(caster).luck = Math.max(-1, Math.min(current + delta, fortune));
    }
  }
}

/** janus.php trigger-entry add_charges_to_ability(): before any effects, once per frame (post_effect_conditions suppresses its copy).
 * Literal integer quantities only; [max] needs PHP's fresh compile and remains a named pre-draw hand-back. */
export function addTriggerCharges(ctx: JanusCtx, ability: Row, caster: Row): void {
  triggerCharges(ctx, ability, caster, true);
}
/** Dependencies must be held even when extreme-dice previews never reach the firing branch. */
export function preflightTriggerCharges(ctx: JanusCtx, ability: Row, caster: Row): void {
  triggerCharges(ctx, ability, caster, false);
}
/** post_effect_conditions.php ~L879 — a DIRECT (non-trigger) use's add_charges_to_ability() (Wizard Major / Minor / Adept / Void Channel,
 *  2026-10-09): after the use's statuses, through the same entry walk, adjust_charges() and the Holding Mastery touch as the trigger entry,
 *  with ft_resolve_ability_charge_qty()'s full quantity: a literal, [x] (a numeric use_ability_variable_1, else menta_level / spell_level,
 *  else 1), [max] (the pool's ceiling), [any] (its current count). [max] on an ability that applies its own status (Open the Void's Void
 *  Opened raises the Mastery ceiling — PHP re-compiles first, ft_refresh_caster_ability_charge_maxes) stays a named hand-back. */
export function addDirectCharges(ctx: JanusCtx, ability: Row, caster: Row, useAbilityVariable1: unknown): void {
  triggerCharges(ctx, ability, caster, true, { variable1: useAbilityVariable1 });
}
/** addDirectCharges' every lookup and named hand-back, settled at janus()'s pre-draw seam (nothing written): the commit capability, the pool
 *  entry, its definition, range-bound, the quantity and the Holding Mastery marker — so none of them surfaces after a die. */
export function directChargesPreDraw(ctx: JanusCtx, ability: Row, caster: Row, useAbilityVariable1: unknown): void {
  if (ctx.trigger || !ctx.read.has(ability, 'add_charges_to_ability')) return;
  if (ctx.statusStacks === undefined) handBack('post_effect_conditions:add_charges_to_ability');
  triggerCharges(ctx, ability, caster, false, { variable1: useAbilityVariable1 });
}
/** The layers through which a use can change its holder's statuses before post_effect_conditions' add_charges_to_ability (a ceiling PHP re-reads). */
const MAX_CEILING_STATUS_LAYERS = ['passive_ability', 'duration_int', 'spell_concentration',
  'status_effect_applied_to_caster_after_cast', 'passive_ability_applied_to_caster_after_cast', 'status_effect_applied_to_caster_on_hit',
  'status_effect_applied_to_target_after_cast', 'passive_ability_applied_to_target_after_cast', 'status_effect_applied_to_target_on_hit',
  'status_effect_applied_to_target_after_contesting_roll_failed',
  'status_effect_removed_from_caster_after_cast', 'status_effect_removed_from_caster_on_hit',
  'status_effect_removed_from_target_after_cast', 'status_effect_removed_from_target_on_hit'];
function directChargeQty(ctx: JanusCtx, ability: Row, caster: Row, raw: unknown, current: number, max: number, variable1: unknown): number {
  if (raw == null || raw === '') return 1; // absent layer → 1
  const token = raw === true ? '[x]' : String(raw).trim();
  if (token === '[x]') {
    // ability_charge_qty_for_x_placeholder(): PHP is_numeric() — never hex / binary / INF (the spell-charge twin above)
    if (typeof variable1 === 'number' ? Number.isFinite(variable1) : typeof variable1 === 'string' && PHP_NUMERIC.test(variable1)) {
      const qty = Math.trunc(Number(variable1));
      if (!Number.isSafeInteger(qty)) handBack('add_charges_to_ability:direct-qty');
      return qty;
    }
    const level = Math.trunc(Number(ctx.read.value(ability, 'menta_level') ?? ctx.read.value(ability, 'spell_level') ?? 0)) || 0;
    return level > 0 ? level : 1;
  }
  if (token === '[max]') {
    // any status this use could add to / remove from its holder (a Self target included) may move the ceiling (Codex review 2026-10-10:
    // removals and on-hit grants too) — named, pre-draw
    if (MAX_CEILING_STATUS_LAYERS.some((key) => ctx.read.has(ability, key))) handBack('add_charges_to_ability:max-ceiling-refresh');
    // ft_refresh_caster_ability_charge_maxes() recompiles before [max]: a status THIS use already landed on the holder (other than the
    // Holding Mastery marker, which carries no ceiling) could have raised it — not modelled, named
    const holder = ecId(caster);
    if (ctx.statusesAdded.some((row) => upper(row.target) === holder && upper(row.status_item_id) !== HOLDING_MASTERY))
      handBack('add_charges_to_ability:max-ceiling-refresh');
    return max;
  }
  if (token === '[any]') return current;
  return phpJsonInt(raw);
}
function triggerCharges(ctx: JanusCtx, ability: Row, caster: Row, apply: boolean, direct?: { variable1: unknown }): void {
  if (!ctx.read.has(ability, 'add_charges_to_ability')) return;
  const raw = ctx.read.value(ability, 'add_charges_qty');
  if (!direct && raw != null && !/^[+-]?\d+$/.test(String(raw))) handBack('add_charges_to_ability:trigger-qty');
  const literalQty = direct ? 1 : raw == null ? 1 : requiredInt(raw, 'add_charges_to_ability:trigger-qty');
  const seen = new Set<string>();
  for (const value of distinctUuids(ctx.read.entries(ability, 'add_charges_to_ability'))) {
    const id = upper(value);
    if (seen.has(id)) continue;
    seen.add(id);
    if (CONSUME_BUCKETS.some(bucket => ctx.read.entries(caster, bucket).some(entry => abilityEntryId(entry) === id))) {
      const definition = ctx.read.definition(id);
      if (!definition) definitionNotHeld('add_charges_to_ability:definition-not-held', id);
      if (id === MASTERY_POOL) masteryMarkerPreDraw(ctx, caster);
      if (record(caster.inventory)?.inventory_id != null) handBack('adjust_charges:inventory-holder');
      if (!ecId(caster)) handBack('adjust_charges:holder-not-held');
      const pool = rawAbilityChargeEntry(ctx, caster, id);
      const qty = direct ? directChargeQty(ctx, ability, caster, raw, pool.current, pool.max, direct.variable1) : literalQty;
      if (ctx.read.has(definition!, 'range_bound_charges') && pool.current + qty > pool.max)
        refuse(`Cannot extend beyond max charges for a Range Bound Charged Ability: ${String(itemField(definition!, 'item_name') ?? '')}`);
      if (apply) adjustCharges(ctx, caster, id, qty, true);
    }
  }
}
